import { BordFilters } from 'bord-design-system';
import { useEffect, useState } from 'react';
import { IOutputQuoteListFilters } from 'types/quote.types';

import './QuoteFilters.scss';

type TFilterId = 'organization' | 'createdAt' | 'responsible' | 'sla' | 'type' | 'country';

export type TQuoteFiltersComp = Record<TFilterId, string[]>;

interface IQuoteFilters {
  filterOptions?: IOutputQuoteListFilters;
  initialFiltersSelected?: TQuoteFiltersComp;
  onChange?: (filters: TQuoteFiltersComp) => void;
}

const filters: { id: TFilterId; name: string }[] = [
  { id: 'country', name: 'País' },
  { id: 'organization', name: 'Cliente' },
  { id: 'responsible', name: 'Responsable' },
  { id: 'sla', name: 'SLA' },
  { id: 'type', name: 'Tipo' }
];

export const QuoteFilters = ({ filterOptions, initialFiltersSelected, onChange }: IQuoteFilters) => {
  const [filterOpened, setFilterOpened] = useState<boolean>(false);
  const [filterId, setFilterId] = useState<TFilterId>();
  const [filtersSelected, setFiltersSelected] = useState<Record<TFilterId, string[]>>(
    initialFiltersSelected ?? ({} as Record<TFilterId, string[]>)
  );

  /**
   * Sincroniza la selección visual cuando los filtros se rehidratan desde la URL.
   */
  useEffect(() => {
    if (!initialFiltersSelected) return;
    setFiltersSelected(initialFiltersSelected);
  }, [initialFiltersSelected]);

  const showFilterList =
    (key: TFilterId) =>
    (value: React.SetStateAction<boolean>): React.SetStateAction<boolean> => {
      setFilterId(key);
      setFilterOpened(value);
      return value;
    };

  const parseFilterOption =
    (filterId: TFilterId) => (option: { name?: string; id?: string | number; code?: string }) => {
      const value = option?.id?.toString() || '';
      return {
        name: option?.name || option?.code || '',
        value,
        id: option?.id,
        icon: null as string | null,
        isActive: filtersSelected[filterId]?.includes(value)
      };
    };

  const getOptions = (filterId: TFilterId) => {
    if (filterId === 'organization') return filterOptions?.organizations?.map(parseFilterOption(filterId)) ?? [];
    if (filterId === 'type') {
      return filterOptions?.quotesTypes?.map(parseFilterOption(filterId)) ?? [];
    }
    if (filterId === 'responsible') {
      return filterOptions?.users?.map(parseFilterOption(filterId)) ?? [];
    }
    if (filterId === 'country') {
      return filterOptions?.countries?.map(parseFilterOption(filterId)) ?? [];
    }
    if (filterId === 'sla') {
      return filterOptions?.quoteSla?.map(parseFilterOption(filterId)) ?? [];
    }

    return [];
  };

  const clearFilters = () => {
    setFiltersSelected({} as Record<TFilterId, string[]>);
    onChange?.({ country: [], organization: [], responsible: [], sla: [], type: [], createdAt: [] });
  };

  return (
    <div className="quoteFilters">
      <BordFilters
        filterIcon="filter"
        filtersContainerStyles={{ width: 'fit-content' }}
        filtersActiveStyle="quoteFiltersActiveStyle"
        customClassNameForFiltersExpandableSelects="quoteFiltersExpandableSelects"
        handleResetFilters={clearFilters}
        filtersList={filters.map(filter => ({
          title: filter.name,
          openFunction: showFilterList(filter.id),
          openStatus: filterOpened && filterId === filter.id,
          rawSelectOptions: getOptions(filter.id),
          handleChangeSelectedOption: option => {
            setFiltersSelected(prev => {
              const set = new Set(prev[filter.id] ?? []);

              if (!option.value) return prev;

              if (set.has(option?.value)) {
                set.delete(option.value);
              } else {
                set.add(option.value);
              }

              const data = {
                ...prev,
                [filter.id]: Array.from(set)
              };

              onChange?.(data);
              return data;
            });
          },

          isFilterActive: Boolean(filtersSelected?.[filter?.id]?.length)
        }))}
      />
    </div>
  );
};
